import { ReactElement, useEffect, useState } from "react";
import { IEAFProduction } from "../../../types";
import Chart from "../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../components/charts/chartUtils";
import { getPalette } from "../../../configuration/configuration";
import * as d3 from "d3";
import KeyValueItem from "../../../components/charts/keyValueItem";
import { formatDateTimeWithSeconds, formatNumber, formatTimeWithSeconds, toTons } from "../../../system/utils";
import HtmlTooltip from "../../../components/charts/tooltip";
import AxisHorizontal from "../../../components/charts/AxisHorizontal";
import CircularProgress from "@mui/material/CircularProgress";
import { DateTime } from "luxon";


interface IProps {
  productionData: IEAFProduction[];
}

interface IMaxValues {
  powerOn: number;
  powerOff: number;
}

const theoreticalPowerOn = 48;
const theoreticalPowerOff = 25;

// const theoreticalFactor = theoreticalPowerOn / (theoreticalPowerOn + theoreticalPowerOff);


const ProductionChart = ({ productionData }: IProps): ReactElement => {
  const chartID = useUniqueId("management-chart");

  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: 90,
    marginBottom: 30,
    marginLeft: 160,
    marginRight: 9,
    marginTop: 10
  });

  const [ maxValues, setMaxValues ] = useState<IMaxValues | null>(null);

  useEffect(() => {
    if (!productionData) {
      return;
    }

    const values: IMaxValues = {
      powerOff: theoreticalPowerOff,
      powerOn: theoreticalPowerOn
    };

    productionData.map(item => {
      values.powerOff = Math.max(values.powerOff, item.powerOFF);
      values.powerOn = Math.max(values.powerOn, item.powerON);
    });

    setMaxValues(values);
  }, [productionData]);


  if(!maxValues) {
    return <div className="text-center"><CircularProgress /></div>;
  }


  const xScale = d3.scaleLinear()
    .domain([productionData[0].beginDatetime, productionData[productionData.length - 1].endDatetime ?? DateTime.now().toSeconds()])
    .range([0, dimensions.boundedWidth]);

  const xAccessorScaled = (d: IEAFProduction) => xScale(d.beginDatetime);
  const widthAccessorScaled = (d: IEAFProduction) => xScale(d.endDatetime ?? DateTime.now().toSeconds()) - xScale(d.beginDatetime);

  const boundedHeight = dimensions.boundedHeight - 2;

  const middle = boundedHeight * maxValues.powerOff / (maxValues.powerOn + maxValues.powerOff);

  const yLineOn = middle + boundedHeight * theoreticalPowerOn / (maxValues.powerOn + maxValues.powerOff);
  const yLineOff = middle - boundedHeight * theoreticalPowerOff / (maxValues.powerOn + maxValues.powerOff);

  return (
    <div className="flex-grow-0" style={{ height: 100, width: "100%" }}>
      <div ref={ref} style={{ height: 120, width: "100%" }}>
        <Chart dimensions={dimensions}>

          <AxisHorizontal
            chartID={chartID}
            dimensions={dimensions}
            scale={xScale}
            label={"Datetime"}
            ticks={getTicks(xScale.domain(), 5)}
            formatTick={(value, index) => index === 0 || index === 4 ? formatDateTimeWithSeconds(value as number) : formatTimeWithSeconds(value as number)}/>


          <rect
            style={{ fill: getPalette().statusColor.empty.background }}
            fillOpacity={0.2}
            x={0}
            y={0}
            width={Math.max(0, dimensions.boundedWidth - 1)}
            height={boundedHeight} />

          <g>
            { productionData.map((heat, index): ReactElement => {
              const x = xAccessorScaled(heat);
              const w = Math.max(0, widthAccessorScaled(heat) - 1);
              const heightFactor = boundedHeight / 100;
              const duration = maxValues?.powerOn + maxValues?.powerOff;
              const powerOnHeight = heightFactor * heat.powerON * 100 / duration;
              const powerOffHeight = heightFactor * heat.powerOFF * 100 / duration;


              return(
                <g key={`${chartID}-${index}`}>

                  <HtmlTooltip title={
                    <div className="display-flex-column align-items-center" >
                      <div className="display-flex-column align-items-center" >
                        <strong>{heat.machine.name}</strong>
                        <strong>{heat.heatNumber}</strong>
                      </div>
                      <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(heat.beginDatetime as number)} />
                      <KeyValueItem name={"End"} value={formatTimeWithSeconds(heat.endDatetime as number)} />
                      <KeyValueItem name={"Steel"} value={heat.steel.code}/>
                      <KeyValueItem name={"Weight"} value={formatNumber(toTons(heat.weight), 2)} unit="US ton" />
                      <KeyValueItem name={"Power ON"} value={formatNumber(heat.powerON, 2)} unit="min" />
                      <KeyValueItem name={"Power OFF"} value={formatNumber(heat.powerOFF, 2)} unit="min" />
                      <KeyValueItem name={"Tap to Tap"} value={formatNumber(heat.tapToTap / 60, 2)} unit="min" />
                    </div>
                  }>
                    <g>
                      <rect
                        style={{ fill: getPalette().statusColor.error.background }}
                        x={x}
                        y={middle - powerOffHeight}
                        width={w}
                        height={powerOffHeight} />

                      <rect
                        style={{ fill: getPalette().statusColor.good.background }}
                        x={x}
                        y={middle}
                        width={w}
                        height={powerOnHeight} />
                    </g>
                  </HtmlTooltip>

                </g>
              );
            })
            }

            <line x1={-150} x2={dimensions.boundedWidth - 1} y1={yLineOn} y2={yLineOn} stroke={"#263238"} strokeWidth={1}
              strokeDasharray="4 2"/>

            <line x1={-150} x2={dimensions.boundedWidth - 1} y1={yLineOff} y2={yLineOff} stroke={"#263238"} strokeWidth={1}
              strokeDasharray="4 2"/>

            <text x={-150} y={yLineOff - 6} fill={getPalette().statusColor.error.background}>Power Off Std.: {theoreticalPowerOff} min</text>
            <text x={-150} y={yLineOn + 16} fill={getPalette().statusColor.good.background}>Power On Std.: {theoreticalPowerOn} min</text>
          </g>
        </Chart>
      </div>
    </div>
  );
};

export default ProductionChart;
